<template>
  <div class="h-full">
    <div class="flex w-full flex-col gap-6">
      <div class="grid grid-cols-2 gap-4 text-[0.8rem]">
        <!-- General Info -->
        <InfusePropertyValue :title="t('common.id')" :title-icon="{ type: 'far', name: 'hashtag' }">
          {{ model?.id }}

          <InfuseCopy v-slot="scope">
            <font-awesome-icon
              class="ml-1.5 cursor-pointer text-neutral-500 hover:text-neutral-600"
              :icon="['far', 'copy']"
              @click="scope.copy(model?.id || '')"
            />
          </InfuseCopy>
        </InfusePropertyValue>

        <InfusePropertyValue v-if="showName" :title="t('common.name')" :title-icon="{ type: 'fal', name: 'building' }">
          {{ model?.name || '-' }}
        </InfusePropertyValue>

        <InfusePropertyValue
          :title="t('common.description')"
          :title-icon="{ type: 'fal', name: 'align-left' }"
          class="col-span-2"
        >
          {{ model?.description || '-' }}
        </InfusePropertyValue>

        <InfusePropertyValue v-if="showCocNumber" :title="t('iam.organisations.cocNumber')" :title-icon="{ type: 'fal', name: 'id-card' }">
          {{ model?.cocNumber || '-' }}
        </InfusePropertyValue>

        <InfusePropertyValue v-if="showType" :title="t('common.type')" :title-icon="{ type: 'fal', name: 'tag' }">
          {{ model?.type || '-' }}
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('common.externalId')" :title-icon="{ type: 'far', name: 'hashtag' }">
          {{ model?.externalId || '-' }}

          <InfuseCopy v-if="model?.externalId" v-slot="scope">
            <font-awesome-icon
              class="ml-1.5 cursor-pointer text-neutral-500 hover:text-neutral-600"
              :icon="['far', 'copy']"
              @click="scope.copy(model?.externalId || '')"
            />
          </InfuseCopy>
        </InfusePropertyValue>

        <InfusePropertyValue
          v-if="showActive"
          :title="t('common.active')"
          :title-icon="{ type: 'fal', name: 'check' }"
        >
          {{ model?.active ? t('common.yes') : t('common.no') }}
        </InfusePropertyValue>

        <!-- Address Section -->
        <div class="col-span-2 mt-2">
          <h3 class="mb-2 text-sm font-semibold">{{ t('common.address_section') }}</h3>
        </div>

        <InfusePropertyValue
          :title="t('common.address')"
          :title-icon="{ type: 'fal', name: 'home' }"
          class="col-span-2"
        >
          {{ model?.address || '-' }}
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('common.postalCode')" :title-icon="{ type: 'fal', name: 'mailbox' }">
          {{ model?.postalCode || '-' }}
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('common.city')" :title-icon="{ type: 'fal', name: 'store' }">
          {{ model?.city || '-' }}
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('common.region')" :title-icon="{ type: 'fal', name: 'location' }">
          {{ model?.region || '-' }}
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('common.country')" :title-icon="{ type: 'fal', name: 'globe' }">
          {{ countryName || '-' }}
        </InfusePropertyValue>

        <!-- Contact Section -->
        <div class="col-span-2 mt-2">
          <h3 class="mb-2 text-sm font-semibold">{{ t('common.contact_section') }}</h3>
        </div>

        <InfusePropertyValue :title="t('common.website')" :title-icon="{ type: 'fal', name: 'globe' }">
          <a
            v-if="model?.website"
            :href="model.website"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary-500 hover:underline"
          >
            {{ model.website }}
            <font-awesome-icon :icon="['far', 'arrow-up-right-from-square']" class="ml-1" />
          </a>
          <span v-else>-</span>
        </InfusePropertyValue>

        <InfusePropertyValue :title="t('common.email')" :title-icon="{ type: 'fal', name: 'envelope' }">
          <a v-if="model?.email" :href="`mailto:${model.email}`" class="text-primary-500 hover:underline">
            {{ model.email }}
          </a>
          <span v-else>-</span>
        </InfusePropertyValue>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { InfuseCopy, InfusePropertyValue } from '@infuse/ui/components';
import { useCountryStore } from '@infuse/ui/stores';
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

export interface EntityWithInfoFields {
  id: string;
  name?: string;
  active?: boolean;
  description?: string;
  cocNumber?: string;
  type?: string;
  externalId?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  region?: string;
  website?: string;
  email?: string;
}

const { t, locale } = useI18n();
const countryStore = useCountryStore();

const props = defineProps<{
  model: EntityWithInfoFields;
  showName?: boolean;
  showActive?: boolean;
  showCocNumber?: boolean;
  showType?: boolean;
}>();

onMounted(() => {
  countryStore.locale = locale.value;
});

watch(locale, (newLocale) => {
  countryStore.locale = newLocale;
});

const countryName = computed(() => {
  if (!props.model?.country) {
    return undefined;
  }
  return countryStore.getCountries.find((c) => c.alpha2 === props.model?.country?.toUpperCase())?.name;
});
</script>
